import {
  Badge,
  Box,
  CellProps,
  Flex,
  Text,
  Tooltip,
  usePagination,
  useTable,
} from '@agrotoken-tech/ui';
import {
  DEBTOR_PLEDGE_STATUSES_ICON,
  debtorCropPledgeStatusBadgeColors,
} from '@bcba/frontend-utils';
import { useTranslation } from '@bcba/react-hooks';
import { ExplorerCropPledgeListDebtorSerializer } from '@bcba/serializers';
import {
  CROP_PLEDGE_USER_ROLES,
  PledgeStatus,
  pledgeStatuses,
} from '@bcba/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from '../../../routes/paths';
import {
  createCropPledge,
  getDebtorCropPledge,
} from '../../../services/crop-pledge';
import { useTableFilters } from '../../../utils/useTableFilters';

export const useDebtorCropPledge = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const {
    formMethods,
    debouncedSearch,
    debouncedStatuses,
    selectedStatuses,
    setSelectedStatuses,
    onFiltersReset,
  } = useTableFilters<PledgeStatus>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: [
      'get-debtor-crop-pledge',
      page,
      debouncedSearch,
      debouncedStatuses,
    ],
    queryFn: () =>
      getDebtorCropPledge({
        page,
        search: debouncedSearch,
        statuses: debouncedStatuses,
      }),
  });

  const { mutate: onCreatePledgeCrop, isPending: isLoadingPledgeCropCreation } =
    useMutation({
      mutationFn: createCropPledge,
      onSuccess: (uuid) => {
        navigate(paths.cropPledgeDetail.replace(':uuid', uuid));
      },
    });

  const columns = useMemo(
    () => [
      {
        Header: t('cropPledge.table.column.internalIdentifier'),
        accessor: 'internalIdentifier',
        Cell: (props: CellProps<ExplorerCropPledgeListDebtorSerializer>) => {
          const { internalIdentifier } = props.row.original;
          return <Text># {internalIdentifier}</Text>;
        },
      },
      {
        Header: t('cropPledge.table.column.companyName'),
        accessor: 'companyName',
        Cell: (props: CellProps<ExplorerCropPledgeListDebtorSerializer>) => (
          <Text
            size={'sm'}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            maxW={'200px'}
          >
            {props.row.original.companyName}
          </Text>
        ),
      },
      {
        Header: t('cropPledge.table.column.fiscalId'),
        accessor: 'companyFiscalId',
      },
      {
        Header: t('cropPledge.table.column.expirationDate'),
        accessor: 'expirationDate',
        Cell: (props: CellProps<ExplorerCropPledgeListDebtorSerializer>) => {
          const { expiresAt } = props.row.original;
          return <Text size="sm">{expiresAt ? expiresAt : '-'}</Text>;
        },
      },
      {
        Header: t('cropPledge.table.column.status'),
        accessor: 'status',
        Cell: (props: CellProps<ExplorerCropPledgeListDebtorSerializer>) => {
          const { statuses = [] } = props.row.original;

          return (
            <Flex gap={0.5}>
              {statuses.map((status) => (
                <Tooltip
                  hasArrow
                  label={t(
                    `cropPledge.status.${CROP_PLEDGE_USER_ROLES.DEBTOR}.${status}.tooltip`,
                  )}
                  title={t(
                    `cropPledge.status.${CROP_PLEDGE_USER_ROLES.DEBTOR}.${status}.tooltip`,
                  )}
                  placement="bottom"
                  bg="white"
                  color="content-secondary"
                  size="sm"
                  minW={'auto'}
                >
                  <Box>
                    <Badge
                      colorScheme={debtorCropPledgeStatusBadgeColors[status]}
                      icon={DEBTOR_PLEDGE_STATUSES_ICON[status] || null}
                      iconPosition="leading"
                    >
                      {t(`cropPledge.status.${status}`)}
                    </Badge>
                  </Box>
                </Tooltip>
              ))}
            </Flex>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const tableData = React.useMemo(
    () => data?.data || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(data)],
  );

  const tableInstance = useTable(
    {
      columns: columns as any,
      data: tableData,
    },
    usePagination,
  );

  const statusOptions = pledgeStatuses.map((s) => ({
    text: t(`cropPledge.status.${s}`),
    value: s,
  }));

  useEffect(() => setPage(1), [debouncedSearch, debouncedStatuses]);

  useEffect(() => {
    return () => {
      queryClient.cancelQueries();
    };
  }, []);

  return {
    formMethods,
    tableInstance,
    isLoading,
    data,
    setPage,
    statusOptions,
    selectedStatuses,
    setSelectedStatuses,
    onFiltersReset,
    onCreatePledgeCrop,
    isLoadingPledgeCropCreation,
  };
};

export default useDebtorCropPledge;
